<template>
  <b-row class="OvertimeView">
    <b-col cols="12" lg="10" offset-lg="1">
      <Notification v-bind.sync="notification" />
      <Notification v-bind.sync="approvalNotification" />

      <OvertimeCard
        :no-submit="true"
        :readonly="true"
        :employee="userEmployeeData"
        :others="otherData"
      />
    </b-col>
  </b-row>
</template>
<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import OvertimeCard from '@/components/OvertimeCard';
import Notification from '@/components/Notification';
import FormService from '@/services/FormService';
import overtimeStatuses from '@/mixins/overtimeStatuses';

const form = new FormService();

export default {
  name: 'OvertimeView',
  mixins: [overtimeStatuses],
  components: {
    Notification,
    OvertimeCard,
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      const { key } = vm.$route.params;
      const { id } = vm.userData;

      vm.loadingOvertimeData = true;
      form.checkIfUser('overtime', id, key)
        .then(({ success, message, isUser }) => {
          if (!success || !isUser) {
            vm.setAlert({
              show: 5,
              variant: 'danger',
              fade: true,
              dismissible: true,
              message: typeof message === 'string' ? message : 'An error occurred',
              icon: {
                name: 'exclamation',
              },
            });
          }
          return success;
        })
        .catch(() => {
          vm.setAlert({
            show: 5,
            variant: 'danger',
            fade: true,
            dismissible: true,
            message: 'An error occurred',
            icon: {
              name: 'exclamation',
            },
          });

          return false;
        })
        .then((success) => {
          if (!success) {
            return vm.$router.push({ name: 'home' });
          }

          // User is the verified approver, get timesheet data
          vm.startLoadingPage();
          return form.getFormDataByKey('overtime', key);
        })
        .then(vm.displayData)
        .then(() => {
          vm.stopLoadingPage();
          vm.loadingOvertimeData = false;
        });
    });
  },
  data() {
    return {
      loadingApproveReject: false,
      loadingOvertimeData: false,
      userEmployeeData: {},
      overtimeData: {},
      confirmModalOptions: {},
      comments: '',
    };
  },
  computed: {
    approvalNotification() {
      const options = { show: !this.loadingOvertimeData };

      const otherOptions = {};
      switch (this.$get(this.overtimeData, 'statusId', -1)) {
        case this.pendingApproval:
          otherOptions.variant = 'light';
          otherOptions.message = 'Overtime application has been submitted for approval.';
          otherOptions.icon = { name: 'regular/hourglass' };
          break;
        case this.approved:
          otherOptions.variant = 'success';
          otherOptions.message = 'Overtime application has been approved.';
          otherOptions.icon = { name: 'check' };
          break;
        case this.rejected:
          otherOptions.variant = 'danger';
          otherOptions.message = 'Overtime application has been rejected.';
          otherOptions.icon = { name: 'times-circle', scale: 1.5 };
          break;
        case this.cancelled:
          otherOptions.variant = 'light ';
          otherOptions.message = 'Overtime application has been cancelled.';
          otherOptions.icon = { name: 'ban' };
          break;
        default:
          otherOptions.variant = 'danger';
          otherOptions.message = 'An error occurred.';
          otherOptions.refresh = true;
          break;
      }

      return Object.assign({}, options, otherOptions);
    },
    otherData() {
      return {
        forApproval: true,
        loadingEmployee: this.loadingUserEmployeeData,
        overtimeData: this.overtimeData,
      };
    },
    approvalAction() {
      return this.$get(this.confirmModalOptions, 'action', '');
    },
    approvalNotifText() {
      const { full_name: name } = this.userEmployeeData;
      return `Overtime application for <strong class="text-uppercase">${name}</strong> is pending your approval.`;
    },
    currentStatus() {
      const status = this.$get(this.overtimeData, 'status', null);
      const statusId = this.$get(this.overtimeData, 'statusId', null);

      return !statusId ? null : { status, id: statusId };
    },
    ...mapGetters(['userData', 'isLoadingRequest']),
    ...mapState(['notification']),
  },
  methods: {
    displayData(result) {
      if (typeof result === 'object' && result) {
        const {
          success,
          user,
          overtime,
          message,
        } = result;

        if (!success) {
          return this.setNotification({
            show: true,
            refresh: true,
            variant: 'danger',
            message: message || 'An error occurred.',
            icon: { name: 'exclamation-triangle' },
          });
        }

        const overtimeData = {};
        const otKeys = Object.keys(overtime || {});
        otKeys.forEach((k) => {
          if (k === 'totalHours' || k === 'nightDiff') {
            overtimeData[k] = Number(overtime[k] || 0).toFixed(2);
          } else {
            overtimeData[k] = overtime[k];
          }
        });

        this.overtimeData = overtimeData;
        this.userEmployeeData = user;
      }
      return false;
    },
    approveOrReject() {
      const action = this.approvalAction;
      if (!action) {
        // TODO: approval action not set
        return this.setAlert({
          show: 5,
          dismissible: true,
          variant: 'danger',
          message: 'Client error',
        });
      }
      // TODO: actual approve/reject
      const { key } = this.$route.params;
      const { id: approverId } = this.userData;

      this.loadingApproveReject = true;
      // Execute approval (or rejection) of overtime
      return form.updateApprovalStatus('overtime', action, key, approverId, this.comments)
        .then(({ success, message }) => {
          const defaultMessage = success ? `overtime request ${action}ed` : 'An error occurred';

          this.setNotification({
            message: message || defaultMessage,
            show: true,
            dismissible: true,
            refresh: !success,
            variant: success ? 'success' : 'danger',
            icon: {
              name: success ? 'check' : 'exclamation-triangle',
            },
          });

          if (success) {
            // TODO: update current overtime status
            switch (action) {
              case 'approve':
                this.overtimeData = {
                  ...this.overtimeData,
                  statusId: this.approved,
                  status: 'approved',
                };
                break;
              case 'reject':
                this.overtimeData = {
                  ...this.overtimeData,
                  statusId: this.rejected,
                  status: 'rejected',
                };
                break;
              default: break;
            }
          }
        })
        .catch(() => {
          this.setNotification({
            show: true,
            dismissible: true,
            refresh: true,
            variant: 'danger',
            message: 'An error occurred',
            icon: {
              name: 'exclamation-triangle',
            },
          });
        })
        .then(() => {
          this.loadingApproveReject = false;
        });
    },
    resetAction() {
      this.confirmModalOptions.action = '';
    },
    ...mapActions([
      'setAlert',
      'setNotification',
      'startLoadingRequest',
      'stopLoadingRequest',
      'startLoadingPage',
      'stopLoadingPage',
    ]),
  },
};
</script>
